/**
 * The hours recorded against a volunteer.
 *
 * Read-only, and that is the design rather than a gap. A volunteer seeing their
 * own hours is how a mistake gets noticed — but letting them edit would undo
 * the point of the approval step, which is that somebody other than the person
 * the hours belong to has checked them. If a figure is wrong, the route is to
 * tell Parinaam, and the screen says so.
 *
 * Approved hours are shown apart from the rest, because they are the ones that
 * count for reporting (§4.29) and "how much have I actually done" is the
 * question this page exists to answer.
 */

import {
  Card,
  EmptyState,
  ErrorState,
  LoadingState,
  PageHeader,
  StatusBadge,
} from '../../components/common';
import { useMyAttendance } from '../../hooks/useAttendance';
import { formatDuration } from '@parinaam-vms/shared';
import { formatDate } from '../../utils/format';
import styles from './MySessionsPage.module.css';

export function MyHoursPage() {
  const attendance = useMyAttendance();

  if (attendance.isPending) return <LoadingState label="Loading your hours…" />;
  if (attendance.isError) {
    return (
      <>
        <PageHeader title="My hours" />
        <Card>
          <ErrorState error={attendance.error} onRetry={() => void attendance.refetch()} />
        </Card>
      </>
    );
  }

  const rows = attendance.data ?? [];
  const approvedMinutes = rows
    .filter((row) => row.status === 'APPROVED')
    .reduce((total, row) => total + row.volunteer_minutes, 0);
  const awaiting = rows.filter((row) => row.status === 'PENDING').length;

  return (
    <>
      <PageHeader title="My hours" />

      {rows.length === 0 ? (
        <Card>
          <EmptyState
            title="No hours recorded yet"
            message="Hours you give will appear here."
          />
        </Card>
      ) : (
        <>
          <Card>
            <div className={styles.totals}>
              <div>
                <p className={styles.totalValue}>{formatDuration(approvedMinutes)}</p>
                <p className={styles.totalLabel}>approved</p>
              </div>
              {awaiting > 0 && (
                <div>
                  <p className={styles.totalValue}>{awaiting}</p>
                  <p className={styles.totalLabel}>
                    {awaiting === 1 ? 'record awaiting a decision' : 'records awaiting a decision'}
                  </p>
                </div>
              )}
            </div>
          </Card>

          <Card title="Every record">
            <ul className={styles.list}>
              {rows.map((row) => (
                <li key={row.attendance_id} className={styles.row} data-status={row.status}>
                  <div className={styles.main}>
                    <p className={styles.name}>{formatDuration(row.volunteer_minutes)}</p>
                    <p className={styles.meta}>
                      {row.session_name} · {row.activity_name}
                    </p>
                    <p className={styles.meta}>{formatDate(row.attendance_date)}</p>
                    {row.status === 'REJECTED' && row.rejection_reason && (
                      <p className={styles.rejected}>{row.rejection_reason}</p>
                    )}
                  </div>
                  <div className={styles.side}>
                    <StatusBadge status={row.status} />
                  </div>
                </li>
              ))}
            </ul>

            <p className={styles.footnote}>
              Tell the team if something looks wrong.
            </p>
          </Card>
        </>
      )}
    </>
  );
}
